import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useCurrentUser } from '@/hooks/useAuth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { approvalsApi, Approval, ApprovalKind, RingiTier } from '@/lib/approvals';
import { forecastsApi } from '@/lib/forecasts';
import { membersApi, memberLabel, WorkspaceMember } from '@/lib/members';
import { isPlanGateError } from '@/lib/planGate';
import { MemberPicker } from '@/components/MemberPicker';
import { UpgradeWall } from '@/components/app/UpgradeWall';
import { AppShell } from '@/components/app/AppShell';

const STATUS_STYLE: Record<Approval['status'], string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-warn-500/10 text-warn-600 border-warn-500/30',
  cancelled: 'bg-foreground/[0.06] text-muted-foreground border-border',
};

export function ApprovalsPage() {
  const { t } = useTranslation();
  const { data: user } = useCurrentUser();
  const { wsId } = useParams<{ wsId: string }>();
  const [searchParams] = useSearchParams();
  const initialKind = (searchParams.get('targetKind') as ApprovalKind | null) ?? undefined;
  const initialTargetId = searchParams.get('targetId') ?? undefined;
  const qc = useQueryClient();

  const { data: approvals = [], isLoading } = useQuery({
    queryKey: ['approvals', wsId],
    queryFn: () => approvalsApi.list(wsId!),
    enabled: !!wsId,
  });

  // Shared with MemberPicker (same query key) — used to resolve signer ids to
  // real names in the cards below.
  const { data: members = [] } = useQuery({
    queryKey: ['members', wsId],
    queryFn: () => membersApi.list(wsId!),
    enabled: !!wsId,
    staleTime: 5 * 60_000,
  });
return (
    <AppShell title={t('approvals.title')} subtitle={t('approvals.subtitle')}>
<main className="mx-auto max-w-5xl px-6 py-10 space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('approvals.title')}</h1>
          <p className="mt-1 text-ink-600">{t('approvals.subtitle')}</p>
        </div>

        <NewApprovalForm wsId={wsId!} t={t} initialKind={initialKind} initialTargetId={initialTargetId} onCreated={() => qc.invalidateQueries({ queryKey: ['approvals', wsId] })} />

        {isLoading && <div className="text-sm text-muted-foreground">{t('approvals.loading')}</div>}

        {!isLoading && approvals.length === 0 && (
          <div className="rounded-md border-2 border-dashed border-border bg-white p-12 text-center">
            <p className="text-ink-700 font-medium">{t('approvals.empty')}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t('approvals.empty_sub')}</p>
          </div>
        )}

        <div className="space-y-3">
          {approvals.map((a) => (
            <ApprovalCard key={a.id} approval={a} currentUserId={user?.id ?? ""} members={members} wsId={wsId!} t={t} onMutated={() => qc.invalidateQueries({ queryKey: ['approvals', wsId] })} />
          ))}
        </div>
      </main>
    </AppShell>
  );
}

type TFn = (k: string, opts?: Record<string, unknown>) => string;

const TARGET_KINDS: ApprovalKind[] = ['forecast', 'dashboard', 'variance', 'board_pack'];

/**
 * Create a workflow. Two routing modes:
 *  - "signers": list the approver user IDs in order (the original behaviour).
 *  - "ringi": enter an amount + threshold tiers; the backend escalates the
 *    approver chain by amount (resolveRingiRoute) — the Japanese 稟議 pattern.
 */
function NewApprovalForm({ wsId, t, onCreated, initialKind, initialTargetId }: { wsId: string; t: TFn; onCreated: () => void; initialKind?: ApprovalKind; initialTargetId?: string }) {
  // Opened pre-filled when another page hands off via ?targetKind=&targetId=
  // (the "Send for approval" buttons on Forecast/Board Pack/etc.).
  const [open, setOpen] = useState(!!initialTargetId);
  const [targetKind, setTargetKind] = useState<ApprovalKind>(initialKind ?? 'board_pack');
  const [targetId, setTargetId] = useState(initialTargetId ?? '');
  const [mode, setMode] = useState<'signers' | 'ringi'>('signers');
  const [signers, setSigners] = useState<string[]>([]);
  const [amount, setAmount] = useState('');
  const [tiers, setTiers] = useState<{ upTo: string; approvers: string[] }[]>([{ upTo: '', approvers: [] }]);

  const create = useMutation({
    mutationFn: () => {
      const base = { targetKind, targetId: targetId.trim() };
      if (mode === 'ringi') {
        const parsedTiers: RingiTier[] = tiers
          .map((row) => ({
            upTo: row.upTo.trim() === '' ? null : Number(row.upTo),
            approvers: row.approvers,
          }))
          .filter((row) => row.approvers.length > 0);
        return approvalsApi.create(wsId, { ...base, amount: Number(amount), tiers: parsedTiers });
      }
      return approvalsApi.create(wsId, { ...base, requiredSigners: signers });
    },
    onSuccess: () => {
      onCreated();
      setOpen(false);
      setTargetId(''); setSigners([]); setAmount(''); setTiers([{ upTo: '', approvers: [] }]);
    },
  });
  const planGated = create.error ? isPlanGateError(create.error) : false;
  const errMsg = create.error && !planGated
    ? ((create.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (create.error as Error).message)
    : null;

  const inputCls = 'w-full rounded-lg border border-border bg-white px-3 py-2 text-sm';

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">
        <Plus className="h-4 w-4" /> {t('approvals.new')}
      </button>
    );
  }

  return (
    <div className="rounded-md border border-border bg-white p-5 space-y-3">
      <h3 className="text-sm font-semibold text-ink-700">{t('approvals.new')}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-xs text-muted-foreground mb-1">{t('approvals.target_kind')}</span>
          <select value={targetKind} onChange={(e) => setTargetKind(e.target.value as ApprovalKind)} className={inputCls}>
            {TARGET_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs text-muted-foreground mb-1">{t('approvals.target_id')}</span>
          {targetKind === 'forecast' ? (
            <ForecastTargetPicker wsId={wsId} value={targetId} onChange={setTargetId} t={t} inputCls={inputCls} />
          ) : (
            <input value={targetId} onChange={(e) => setTargetId(e.target.value)} className={`num ${inputCls}`} placeholder={t('approvals.target_id_hint')} />
          )}
        </label>
      </div>

      <div className="flex gap-2 text-xs">
        <button onClick={() => setMode('signers')} className={'px-3 py-1.5 rounded-md border ' + (mode === 'signers' ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold' : 'border-border text-ink-600')}>
          {t('approvals.mode_signers')}
        </button>
        <button onClick={() => setMode('ringi')} className={'px-3 py-1.5 rounded-md border ' + (mode === 'ringi' ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold' : 'border-border text-ink-600')}>
          {t('approvals.mode_ringi')}
        </button>
      </div>

      {mode === 'signers' ? (
        <label className="block">
          <span className="block text-xs text-muted-foreground mb-1">{t('approvals.signers_label')}</span>
          <MemberPicker
            wsId={wsId}
            value={signers}
            onChange={setSigners}
            ordered
            addLabel={t('approvals.signers_add', { defaultValue: 'Add a signer…' })}
            emptyLabel={t('approvals.signers_empty', { defaultValue: 'No signers yet — add them in signing order.' })}
          />
        </label>
      ) : (
        <div className="space-y-3">
          <label className="block max-w-xs">
            <span className="block text-xs text-muted-foreground mb-1">{t('approvals.amount')}</span>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className={`num ${inputCls}`} placeholder="5000000" />
          </label>
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{t('approvals.tiers_label')}</span>
              <button onClick={() => setTiers((ts) => [...ts, { upTo: '', approvers: [] }])} className="text-xs text-emerald-700 hover:text-emerald-800 font-semibold inline-flex items-center gap-1">
                <Plus className="h-3.5 w-3.5" /> {t('approvals.tier_add')}
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">{t('approvals.tiers_hint')}</p>
            <div className="mt-2 space-y-2">
              {tiers.map((row, i) => (
                <div key={i} className="flex items-start gap-2 rounded-md border border-border/70 bg-ink-50/40 p-2">
                  <label className="block shrink-0">
                    <span className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">{t('approvals.tier_upto')}</span>
                    <input
                      type="number"
                      value={row.upTo}
                      onChange={(e) => setTiers((ts) => ts.map((r, idx) => idx === i ? { ...r, upTo: e.target.value } : r))}
                      placeholder={t('approvals.tier_upto')}
                      className={`num ${inputCls} max-w-[150px]`}
                    />
                  </label>
                  <div className="flex-1 min-w-0">
                    <span className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">{t('approvals.tier_approvers')}</span>
                    <MemberPicker
                      wsId={wsId}
                      value={row.approvers}
                      onChange={(ids) => setTiers((ts) => ts.map((r, idx) => idx === i ? { ...r, approvers: ids } : r))}
                      ordered
                      addLabel={t('approvals.tier_approvers_add', { defaultValue: 'Add an approver…' })}
                      emptyLabel={t('approvals.tier_approvers_empty', { defaultValue: 'No approvers for this tier yet.' })}
                    />
                  </div>
                  <button onClick={() => setTiers((ts) => ts.filter((_, idx) => idx !== i))} className="text-ink-400 hover:text-warn-600 p-1 shrink-0" aria-label={t('common.delete')}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {planGated && (
        <UpgradeWall wsId={wsId} requiredTier="growth" featureName={t('approvals.feature_name', { defaultValue: 'Approval workflows' })} />
      )}

      <div className="flex gap-2">
        <button onClick={() => create.mutate()} disabled={create.isPending || !targetId.trim() || (mode === 'signers' ? signers.length === 0 : tiers.every((r) => r.approvers.length === 0))} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60">
          {create.isPending ? t('approvals.creating') : t('approvals.create')}
        </button>
        <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg border border-border text-sm text-ink-700 hover:bg-foreground/[0.04]">
          {t('common.cancel')}
        </button>
        {errMsg && <span className="self-center text-xs text-warn-700">{errMsg}</span>}
      </div>
    </div>
  );
}

/**
 * Replaces the raw target-id UUID field when the kind is 'forecast' — pick a
 * real scenario instead of pasting an id. A prefilled id that isn't in the list
 * (e.g. handed off from another page) is preserved as its own option so the
 * target is never silently dropped.
 */
function ForecastTargetPicker({ wsId, value, onChange, t, inputCls }: { wsId: string; value: string; onChange: (id: string) => void; t: TFn; inputCls: string }) {
  const { data: forecasts = [], isLoading } = useQuery({
    queryKey: ['forecasts', wsId],
    queryFn: () => forecastsApi.list(wsId),
    enabled: !!wsId,
  });
  const known = forecasts.some((f) => f.id === value);
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} disabled={isLoading}>
      <option value="">{isLoading ? t('common.loading') : t('approvals.target_pick_forecast', { defaultValue: 'Select a forecast…' })}</option>
      {value && !known && <option value={value}>{value}</option>}
      {forecasts.map((f) => <option key={f.id} value={f.id}>{f.scenarioLabel}</option>)}
    </select>
  );
}

function ApprovalCard({ approval, currentUserId, members, wsId, onMutated, t }: { approval: Approval; currentUserId: string; members: WorkspaceMember[]; wsId: string; onMutated: () => void; t: (k: string, opts?: Record<string, unknown>) => string }) {
  const [note, setNote] = useState('');
  const isMyTurn = approval.status === 'pending' && approval.requiredSigners[approval.currentStep] === currentUserId;

  // Resolve signer ids to real names; fall back to the raw id (e.g. legacy
  // seed data like "u1") so nothing disappears.
  const byId = new Map(members.map((m) => [m.id, m]));
  const signerName = (id: string) => {
    const m = byId.get(id);
    return m ? memberLabel(m) : id;
  };

  const act = useMutation({
    mutationFn: (action: 'approve' | 'reject') => approvalsApi.act(approval.id, { action, note: note || undefined }),
    onSuccess: () => { setNote(''); onMutated(); },
  });
  const actErrorMsg = act.error
    ? ((act.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (act.error as Error).message)
    : null;

  const statusLabel = t(`approvals.status_${approval.status}`);

  // Let a reviewer open what they're approving. board_pack/variance/dashboard
  // route to their page; forecast to the Forecast page.
  const targetHref = (() => {
    switch (approval.targetKind) {
      case 'forecast': return `/app/workspace/${wsId}/forecast`;
      case 'board_pack': return `/app/workspace/${wsId}/board-pack`;
      case 'variance':
      case 'dashboard': return `/app/workspace/${wsId}`;
      default: return null;
    }
  })();

  return (
    <div className="rounded-md border border-border bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="num text-sm text-muted-foreground">{approval.targetKind}</span>
            <span className="num text-xs text-ink-400">·</span>
            {targetHref ? (
              <Link to={targetHref} className="num text-sm text-emerald-700 hover:text-emerald-800 hover:underline" title={t('approvals.open_target', { defaultValue: 'Open the item being approved' })}>
                {approval.targetId}
              </Link>
            ) : (
              <span className="num text-sm text-ink-700">{approval.targetId}</span>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground num">
            {t('approvals.step_of', { current: Math.min(approval.currentStep + 1, approval.requiredSigners.length), total: approval.requiredSigners.length })}
            {approval.amount != null && <> · {t('approvals.amount')}: {approval.amount.toLocaleString()}</>}
            {' · '}{t('approvals.created', { when: new Date(approval.createdAt).toLocaleString() })}
          </p>
        </div>
        <span className={`text-xs font-semibold uppercase tracking-wide rounded-md px-2 py-0.5 border ${STATUS_STYLE[approval.status]}`}>
          {statusLabel}
        </span>
      </div>

      <ol className="mt-4 space-y-1.5">
        {approval.requiredSigners.map((signerId, i) => {
          const decision = approval.history.find((h) => h.step === i);
          return (
            <li key={i} className="flex items-center gap-2 text-sm">
              <span className={`num text-xs w-6 ${i < approval.currentStep ? 'text-emerald-600' : i === approval.currentStep && approval.status === 'pending' ? 'text-emerald-700' : 'text-ink-400'}`}>
                {decision?.action === 'approved' ? '✓' : decision?.action === 'rejected' ? '✗' : i === approval.currentStep && approval.status === 'pending' ? '●' : '○'}
              </span>
              <span className="text-ink-700 text-xs truncate" title={signerId}>{signerName(signerId)}</span>
              {decision && (
                <span className="text-xs text-muted-foreground ml-2">
                  {decision.action} {new Date(decision.at).toLocaleDateString()}
                  {decision.note && <span className="italic text-ink-500"> — “{decision.note}”</span>}
                </span>
              )}
            </li>
          );
        })}
      </ol>

      {isMyTurn && (
        <div className="mt-4 rounded-xl bg-accent-50 border border-accent-100 p-3">
          <p className="text-xs text-accent-700 font-semibold mb-2">{t('approvals.your_turn')}</p>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('approvals.note_placeholder')}
            rows={2}
            className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm"
          />
          <div className="mt-2 flex gap-2">
            <button
              onClick={() => act.mutate('approve')}
              disabled={act.isPending}
              className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"
            >
              {t('approvals.approve')}
            </button>
            <button
              onClick={() => act.mutate('reject')}
              disabled={act.isPending}
              className="px-3 py-1.5 rounded-md bg-warn-500 text-white text-sm font-semibold hover:bg-warn-600 disabled:opacity-60"
            >
              {t('approvals.reject')}
            </button>
          </div>
          {actErrorMsg && (
            <p className="mt-2 text-xs text-warn-700">{actErrorMsg}</p>
          )}
        </div>
      )}
    </div>
  );
}
